import { canAuditMessenger, canInspectAccess, canManageAi, canManageUsers } from "@/lib/auth/rbac";
import type { SessionRole } from "@/lib/auth/types";
import { formatSpendAmount } from "@/lib/ai/spend-format";
import type { AiSpendCurrency } from "@/lib/ai/spend-cost";
import { aiSettingsCopy } from "@/lib/ui/ai-settings";
import {
  adminChannelsHref,
  channelsCopy,
} from "@/lib/ui/channels";
import {
  adminPackagesHref,
  knowledgePackageCopy,
} from "@/lib/ui/knowledge-packages";
import {
  adminDepartmentsHref,
  adminGroupsHref,
  adminOrganizationHref,
  orgCopy,
} from "@/lib/ui/org";

export const usersCopy = {
  title: "Пользователи",
  description: "Сотрудники этой организации. Новый пользователь сразу может войти.",
  invite: "Добавить сотрудника",
  cancel: "Отмена",
  pending: "Сохраняем…",
  errorTitle: "Пользователи",
  denied: "Недостаточно прав",
  invalidEmail: "Укажите корректный email",
  invalidName: "Укажите имя",
  invalidPassword: "Пароль не короче 8 символов",
  invalidRole: "Выберите роль",
  emailTaken: "Пользователь с таким email уже есть",
  created: "Сотрудник добавлен",
  save: "Сохранить",
  saved: "Сохранено",
  roleLabel: "Роль",
  rolesTitle: "Роли",
  rolesHelp: "Можно отметить несколько. Без отметок остаётся обычный сотрудник.",
  lastAdmin: "Нельзя снять последнего администратора",
  missingUser: "Сотрудник не найден",
  missingDepartment: "Выберите отдел",
  missingGroup: "Выберите группу",
  emptyDepartments: "Не состоит ни в одном отделе.",
  emptyGroups: "Не состоит ни в одной группе.",
  departmentsTitle: "Отделы",
  groupsTitle: "Группы",
  quotaTitle: "Лимит AI",
  quotaLabel: "Лимит, $ / месяц",
  quotaLabelRub: "Лимит, ₽ / месяц",
  invalidQuota: "Лимит: число от 0 до 100000, не больше двух знаков после точки.",
  backUsers: "К пользователям",
  searchLabel: "Найти сотрудника",
  searchPlaceholder: "Имя или email",
  emptyFilterTitle: "Никого не найдено",
  emptyFilterDescription: "Измените запрос.",
  columnName: "Имя",
  columnEmail: "Email",
  columnDepartments: "Отделы",
  columnRole: "Роль",
  open: "Открыть",
  nav: "Администрирование",
  navUsers: "Пользователи",
  navAi: "AI",
  navChannels: "Мессенджеры",
} as const;

export const ROLE_LABELS: Record<SessionRole, string> = {
  ADMIN: "Администратор",
  MANAGER: "Менеджер",
  USER: "Сотрудник",
  AI_ADMIN: "AI-администратор",
  KNOWLEDGE_EDITOR: "Редактор знаний",
  AUDITOR: "Аудитор",
};

export const ROLE_DESCRIPTIONS: Record<SessionRole, string> = {
  USER: "Документы, поиск, чат, Wiki и агенты — в пределах выданного доступа. Админку не видит.",
  MANAGER:
    "Как сотрудник, плюс может назначить документ в любой отдел при загрузке. На главной видит расходы коллег на AI.",
  AI_ADMIN:
    "Настраивает лимит ответа, ожидание Wiki и квоты сотрудников. Модели задаёт платформа.",
  KNOWLEDGE_EDITOR:
    "Проверяет факты Wiki: очередь «На проверку», правка формулировки, подтверждение и отклонение.",
  AUDITOR:
    "Контроль переписок ботов и проверка доступа к файлу. Рабочие экраны как у сотрудника, без настроек организации, AI и очереди Wiki.",
  ADMIN:
    "Полное управление организацией: сотрудники, отделы, группы, доступы, AI и проверка Wiki.",
};

export function roleLabel(role: SessionRole): string {
  return ROLE_LABELS[role];
}

export function roleDescription(role: SessionRole): string {
  return ROLE_DESCRIPTIONS[role];
}

export function userQuotaHelp(
  orgQuota: number | null,
  currency: AiSpendCurrency = "USD",
): string {
  if (orgQuota == null) {
    return "Пусто — без лимита.";
  }
  return `Пусто — ${formatSpendAmount(orgQuota, currency)} в месяц, как у организации.`;
}

export function formatUserRoles(roles: SessionRole[]): string {
  const labels = roles.map(roleLabel);
  return labels.length > 0 ? labels.join(", ") : ROLE_LABELS.USER;
}

export function filterAdminUsers<T extends { name: string; email: string }>(
  rows: T[],
  query: string,
): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return rows;
  }
  return rows.filter(
    (row) =>
      row.name.toLowerCase().includes(needle) ||
      row.email.toLowerCase().includes(needle),
  );
}

export function adminUsersHref(): string {
  return "/admin/users";
}

export function adminUserHref(id: string): string {
  return `/admin/users/${id}`;
}

export function formatUserMeta(
  email: string,
  departments: readonly string[],
): string {
  return [email, departments.join(", ")].filter(Boolean).join(" · ");
}

export function adminAiHref(): string {
  return "/settings/ai";
}

export function adminHref(): string {
  return "/admin";
}

export type AdminMenuAccess = {
  canManageUsers: boolean;
  canManageAi: boolean;
  canAuditMessenger?: boolean;
  canInspectAccess?: boolean;
};

export function adminMenuAccess(roles: SessionRole[]): AdminMenuAccess {
  return {
    canManageUsers: canManageUsers(roles),
    canManageAi: canManageAi(roles),
    canAuditMessenger: canAuditMessenger(roles),
    canInspectAccess: canInspectAccess(roles),
  };
}

export type AdminMenuItem = {
  href: string;
  label: string;
  description: string;
};

export type AdminCrumb = {
  label: string;
  href?: string;
};

export function adminMenuItems(access: AdminMenuAccess): AdminMenuItem[] {
  const items: AdminMenuItem[] = [];
  if (access.canManageUsers) {
    items.push(
      {
        href: adminOrganizationHref(),
        label: orgCopy.navOrganization,
        description: orgCopy.organizationDescription,
      },
      {
        href: adminUsersHref(),
        label: usersCopy.navUsers,
        description: usersCopy.description,
      },
      {
        href: adminDepartmentsHref(),
        label: orgCopy.navDepartments,
        description: orgCopy.departmentsDescription,
      },
      {
        href: adminGroupsHref(),
        label: orgCopy.navGroups,
        description: orgCopy.groupsDescription,
      },
      {
        href: adminChannelsHref(),
        label: usersCopy.navChannels,
        description: channelsCopy.description,
      },
      {
        href: adminPackagesHref(),
        label: knowledgePackageCopy.tenantAdminNav,
        description: knowledgePackageCopy.tenantAdminDescription,
      },
    );
  }
  if (access.canInspectAccess) {
    items.push({
      href: "/admin/access",
      label: "Доступ",
      description: "Сотрудник и файлы: разрешено или нет. Коллекцию или файл можно не выбирать.",
    });
  }
  if (access.canManageAi) {
    items.push({
      href: adminAiHref(),
      label: usersCopy.navAi,
      description: aiSettingsCopy.description,
    });
  }
  return items;
}

export function adminMenuSelectedIndex(
  pathname: string,
  items: readonly { href: string }[],
): number {
  let selected = 0;
  let best = -1;
  items.forEach((item, index) => {
    const matches =
      pathname === item.href || pathname.startsWith(`${item.href}/`);
    if (matches && item.href.length > best) {
      selected = index;
      best = item.href.length;
    }
  });
  return selected;
}

export function adminHomeHref(access: AdminMenuAccess): string {
  return adminMenuItems(access)[0]?.href ?? "/";
}

export function adminTrail(...crumbs: AdminCrumb[]): AdminCrumb[] {
  return [{ label: usersCopy.nav, href: adminHref() }, ...crumbs];
}

export function isAdminEntityPath(pathname: string): boolean {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] !== "admin") {
    return false;
  }
  if (
    (parts[1] === "users" ||
      parts[1] === "departments" ||
      parts[1] === "groups") &&
    parts.length === 3
  ) {
    return true;
  }
  if (
    parts[1] === "channels" &&
    parts[2] === "transcripts" &&
    parts.length === 3
  ) {
    return true;
  }
  return (
    parts[1] === "channels" &&
    (parts[2] === "contacts" ||
      parts[2] === "audiences" ||
      parts[2] === "bots" ||
      parts[2] === "transcripts") &&
    parts.length === 4
  );
}

export function adminFrameClassName(): string {
  return "admin-page page-stack page-stack-wide";
}
